import { useEffect } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { WorkSettingsSelection } from "../../../../shared/work-settings";
import type { WorkflowSectionContext, WorkflowSectionView } from "../types";
import { conversationKey, useConversationPanel } from "../panel";
import { WorkSettingsPanel } from "./WorkSettingsPanel";
import { useWorkSettings } from "./useWorkSettings";

/** The work settings panel, starting a turn through the chat's own send. */
export function useWorkSettingsView(ctx: WorkflowSectionContext): WorkflowSectionView {
  const { locale, t } = useLocale();
  const { scope, host, busy, blocked, draft, panelOpen: open } = ctx;
  const scopeKey = conversationKey(scope);
  useConversationPanel(ctx);
  const settings = useWorkSettings(
    ctx.enabled && scope.token && scope.orgId && scope.projectId && scope.userId
      ? { token: scope.token, orgId: scope.orgId, projectId: scope.projectId, userId: scope.userId, threadId: scope.threadId }
      : null,
    scopeKey,
    open || scope.threadId !== null,
    ctx.workflowId ?? null,
  );

  // Entered from its card, the workflow is already chosen: the form opens on it.
  const entered = ctx.workflowId;
  const { definitions, workflowId: chosen, applied, selectWorkflow } = settings;
  useEffect(() => {
    if (!entered || scope.threadId !== null || applied || chosen === entered) return;
    if (definitions.some((item) => item.workflowId === entered)) selectWorkflow(entered);
    // `selectWorkflow` is rebuilt every render; the values above decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entered, scope.threadId, applied, chosen, definitions]);

  function start(text: string, selection: WorkSettingsSelection) {
    if (busy || blocked) return;
    settings.setApplied(selection);
    const request = draft.trim() ? `${text}\n\n${t("workSettings.currentRequest")}\n${draft}` : text;
    ctx.send(request, {
      attachments: ctx.composerAttachments(),
      body: { work_settings: selection },
      // Accepted: what the composer held went into this turn, and the selection is spent.
      onAccepted: () => {
        ctx.clearComposer();
        settings.clear();
      },
    });
    ctx.setPanelOpen(false);
  }

  // The forced run mode and Save Skill are the workflow's declared facts (`GET /workflows`,
  // read by the pane), not this section's to decide.
  return {
    // The initial snapshot identifies the workflow without changing message content.
    messageTitle: (settings.snapshot?.definition ?? settings.definitions.find(
      item => item.workflowId === settings.applied?.workflowId,
    ))?.locales[locale === "zh" ? "zh-TW" : "en"].title,
    composerLocked: !!settings.applied && busy,
    rail: {
      label: t("workSettings.title"),
      disabled: !scope.projectId,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <path d="M4 7h16M4 17h16M8 4v6M16 14v6" />
        </svg>
      ),
    },
    panel: open ? <WorkSettingsPanel
      key={scopeKey}
      state={settings}
      workflowDescription={ctx.workflowDescription}
      historical={scope.threadId !== null}
      controls={ctx.runControls}
      // Start is the consent to a run that does not ask at each step: said behind the glyph beside the model picker (owner, 2026-10-10).
      notice={ctx.forcedMode === "auto" ? t("workSettings.autoNotice") : null}
      disabled={busy || blocked}
      host={{ ...host, token: scope.token, orgId: scope.orgId }}
      documentContext={ctx.documentContext}
      onApply={start}
    /> : null,
  };
}
